import { HttpClient, HttpEvent, HttpEventType, HttpRequest } from '@angular/common/http';
import { saveAs } from 'file-saver';
import { Observable } from 'rxjs';
import { filter, map, share } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { usingInternetExplorer } from '../browser-helpers/browser-helper';

import { showErrorSwal } from '../validation-helpers/show-error-swal';
import { getContentDisposition } from './get-content-disposition';

export function defaultProgressChangedEvent(progress: number) {
	const element = Swal.getHtmlContainer()?.querySelector('.download-progress');
	// There is an occasional race-condition where Swal.getHtmlContainer() is called too early and
	// return null|undefined.
	if (element) {
		element.textContent = `${progress}%`;
	}
}
export interface DownloadFileOptions {
	/** Fires when the download progress has changed */
	onProgressChanged?: (number) => void;
	loadingSwalHtml?: string;
	showLoadingSwal?: boolean;
	errorHandler?: (string) => void;
	/** Whether to auto save the downloaded file to disk */
	autoSave?: boolean;
}

export interface DownloadFileResult {
	httpEvent: HttpEvent<unknown>;
	fileData: {
		body: unknown;
		filename: string;
	};
}

/**
 * Starts a file download with progress indicator
 * Returns as soon as the request is complete
 * @param http Instance of HTTP Client
 * @param url The url which returns the file content
 * @param options Options for this method
 * @returns
 */
export function downloadFile(http: HttpClient, url: string, options?: DownloadFileOptions): Observable<DownloadFileResult> {
	// Assign defaults
	options = Object.assign(
		<DownloadFileOptions>{
			onProgressChanged: defaultProgressChangedEvent,
			loadingSwalHtml: 'Downloading file... <span class="download-progress" style="font-weight: bold"></span>',
			showLoadingSwal: true,
			errorHandler: result => {
				showErrorSwal(result);
			},
			autoSave: true,
		},
		options || {}
	);

	const download$ = http
		.request(
			new HttpRequest('GET', url, {
				reportProgress: true,
				responseType: 'blob',
			})
		)
		.pipe(
			map(event => {
				const newEvent = {
					httpEvent: event,
					fileData: null,
				};

				if (event.type === HttpEventType.Response) {
					const filename = getContentDisposition(event.headers).filename;
					if (filename) {
						newEvent.fileData = {
							body: event.body,
							filename: filename,
						};
					} else {
						throw Error('Error downloading file.');
					}
				} else if (event.type === HttpEventType.DownloadProgress) {
					const progress = Math.round(100 * (event.loaded / event.total));
					options.onProgressChanged(progress);
				}

				return newEvent;
			}),
			filter(event => event.httpEvent.type === HttpEventType.Response),
			share()
		);

	// Show loading swal
	if (options.showLoadingSwal) {
		Swal.fire({
			html: options.loadingSwalHtml,
			willOpen: () => {
				Swal.showLoading();
			},
		});
	}

	download$.subscribe(
		(event: DownloadFileResult) => {
			if (options.autoSave === true) {
				if (usingInternetExplorer()) {
					(window.navigator as any).msSaveOrOpenBlob(event.fileData.body, event.fileData.filename);
				} else {
					saveAs(event.fileData.body, event.fileData.filename);
				}
			}

			Swal.close();
			Swal.fire({
				icon: 'success',
				html: 'Download finished!',
				timer: 2000,
			});
		},
		(err: any) => {
			// Read the error message from the blob response
			const reader = new FileReader();
			reader.onload = () => {
				let parsedResult = <string>reader.result;
				try {
					parsedResult = JSON.parse(parsedResult);
				} catch {}

				options.errorHandler(parsedResult);
			};
			reader.readAsText(err.error);
			Swal.close();
		}
	);

	return download$;
}
